import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Drawer,
  Dropdown,
  Input,
  Modal,
  Row,
  Segmented,
  Space,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  message
} from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faCircleCheck,
  faCommentSms,
  faFileLines,
  faGear,
  faHeadphones,
  faHeadset,
  faMagnifyingGlass,
  faPhone,
  faPhoneVolume,
  faRobot,
  faStethoscope,
  faUserPlus,
  faUsers,
  faVial,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import caseService from '../../../services/CaseService';
import { COLORS } from '../../../constants/colors';
import LiveCallDrawer from './LiveCallDrawer';
import MemberCardDrawer from './MemberCardDrawer';
import VoiceSettings from './VoiceSettings';
import {
  CaseTable,
  ConfigChip,
  MemberCell,
  NextActionButton,
  OutreachHeartLoader,
  QueueHeader,
  QueueStatCard,
  QueueToolbar,
  ReasonCell,
  RiskPill,
  RowActionButton,
  SmsPreview,
  StepDot,
  StepTrack
} from '../styled';

const { TextArea } = Input;

const RISK_STYLES = {
  critical: { color: '#a8071a', bg: '#fff1f0' },
  high: { color: '#d46b08', bg: '#fff7e6' },
  medium: { color: '#ad8b00', bg: '#fffbe6' },
  low: { color: COLORS.$deepTiffany, bg: '#E6FAF8' },
  minimal: { color: COLORS.$deepTiffany, bg: '#E6FAF8' },
  unknown: { color: '#8c8c8c', bg: '#f5f5f5' }
};
const SOURCES = {
  'kit-results': { label: 'Lab kit results', icon: faVial },
  screening: { label: 'Screening', icon: faStethoscope },
  manual: { label: 'Added by staff', icon: faUserPlus }
};
const LIVE_STATUSES = ['initiated', 'ringing', 'in-progress'];
const SAMPLE_NAME = 'Jordan';

const timeAgo = (d) => {
  if (!d) return '';
  const mins = (Date.now() - new Date(d).getTime()) / 60000;
  if (mins < 1) return 'just now';
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  if (mins < 10080) return `${Math.floor(mins / 1440)}d ago`;
  return new Date(d).toLocaleDateString();
};

const formatPhone = (p) => {
  const digits = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : (p || '');
};

const initials = (name) =>
  String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('') || '?';

const renderText = (template, firstName) =>
  String(template || '').replace(/\{\{\s*firstName\s*\}\}/g, firstName || 'there');

function callStep (call) {
  if (!call) return { state: 'todo', label: 'AI call' };
  if (LIVE_STATUSES.includes(call.status)) return { state: 'live', label: 'Calling…' };
  if (call.status === 'no-answer' || call.status === 'busy') return { state: 'warn', label: 'No answer' };
  if (call.status === 'failed') return { state: 'warn', label: 'Call failed' };
  if (['exhausted', 'no_dob_on_file'].includes(call.verificationOutcome)) return { state: 'warn', label: 'Not verified' };
  return { state: 'done', label: 'Called' };
}

/**
 * Case Management: members whose latest testing scored high/critical (or who
 * staff added). Step 1 sends the pre-call text, step 2 starts the AI call that
 * verifies name + DOB and walks them through their results.
 */
const CallQueueTab = ({ onCountChange }) => {
  const [memberCard, setMemberCard] = useState(null);
  const [view, setView] = useState('active');
  const [stage, setStage] = useState('all');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('calling');
  const [transferNumber, setTransferNumber] = useState('');
  const [textTemplate, setTextTemplate] = useState('');
  const [inboundMode, setInboundMode] = useState('browser');
  const [ringSeconds, setRingSeconds] = useState(20);
  const [missedTextEnabled, setMissedTextEnabled] = useState(false);
  const [missedTextTemplate, setMissedTextTemplate] = useState('');
  const [connectingNumber, setConnectingNumber] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [textModal, setTextModal] = useState(null);
  const [liveCaseId, setLiveCaseId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, view, stage]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const params = { view, page, pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      if (view === 'active' && stage !== 'all') params.stage = stage;
      const res = await caseService.getCallQueue(params);
      if (!res?.success) throw new Error(res?.error);
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      if (view === 'active') {
        setCounts(res.data.counts || null);
        onCountChange?.(res.data.counts?.total ?? 0);
      }
    } catch (err) {
      if (!quiet) message.error(err.message || 'Failed to load Case Management');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [view, stage, page, pageSize, debouncedSearch, onCountChange]);

  useEffect(() => { load(); }, [load]);
  const refreshQuiet = useCallback(() => load({ quiet: true }), [load]);

  // Poll while an AI call is live so its outcome shows up without a refresh.
  const hasLiveCall = rows.some((r) => LIVE_STATUSES.includes(r.lastCall?.status));
  useEffect(() => {
    if (!hasLiveCall) return undefined;
    const t = setInterval(() => load({ quiet: true }), liveCaseId ? 5000 : 10000);
    return () => clearInterval(t);
  }, [hasLiveCall, load, liveCaseId]);

  const applySettings = (data) => {
    setSettings(data);
    setTransferNumber(formatPhone(data.transferPhoneNumber));
    setTextTemplate(data.preCallTextTemplate || '');
    setInboundMode(data.inboundMode || 'browser');
    setRingSeconds(data.inboundRingSeconds || 20);
    setMissedTextEnabled(!!data.missedCallTextEnabled);
    setMissedTextTemplate(data.missedCallTextTemplate || '');
  };

  const loadSettings = useCallback(() => {
    caseService.getCallQueueSettings().then((res) => {
      if (res?.success) applySettings(res.data);
    });
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await caseService.updateCallQueueSettings({
        transferPhoneNumber: transferNumber,
        preCallTextTemplate: textTemplate,
        inboundMode,
        inboundRingSeconds: ringSeconds,
        missedCallTextEnabled: missedTextEnabled,
        missedCallTextTemplate: missedTextTemplate
      });
      if (!res?.success) throw new Error(res?.error);
      applySettings(res.data);
      setSettingsOpen(false);
      message.success('Call settings saved');
    } catch (err) {
      message.error(err.message || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const connectNumber = async () => {
    setConnectingNumber(true);
    try {
      const res = await caseService.connectInboundNumber();
      if (!res?.success) throw new Error(res?.error);
      setSettings((s) => ({ ...s, inbound: res.data }));
      message.success('Inbound calls now come to this system');
    } catch (err) {
      message.error(err.message || 'Could not connect the phone number');
    } finally {
      setConnectingNumber(false);
    }
  };

  const withBusy = async (caseId, fn) => {
    setBusy((b) => ({ ...b, [caseId]: true }));
    try {
      await fn();
    } finally {
      setBusy((b) => ({ ...b, [caseId]: false }));
    }
  };

  const openTextModal = (record) => {
    setTextModal({ record, body: renderText(settings?.preCallTextTemplate, record.name.split(' ')[0]) });
  };

  const sendText = () => withBusy(textModal.record.caseId, async () => {
    const res = await caseService.sendPreCallText(textModal.record.caseId, textModal.body);
    if (!res?.success) {
      message.error(res?.error || 'Failed to send the text');
      return;
    }
    message.success(`Text sent to ${textModal.record.name}`);
    setTextModal(null);
    load({ quiet: true });
  });

  const placeCall = (record, allowMissingConsent) => withBusy(record.caseId, async () => {
    const res = await caseService.startResultsCall(record.caseId, { allowMissingConsent });
    if (res?.code === 'MISSING_CALL_CONSENT') {
      confirmWithoutConsent(record);
      return;
    }
    if (!res?.success) {
      message.error(res?.error || 'Failed to start the call');
      return;
    }
    message.success(`Calling ${record.name}…`);
    await load({ quiet: true });
    setLiveCaseId(record.caseId);
  });

  const confirmWithoutConsent = (record) => {
    Modal.confirm({
      title: 'AI call consent wasn\'t checked',
      content: `${record.name} didn't check the AI voice call consent on their intake form. You can still start the call.`,
      okText: 'Call anyway',
      cancelText: 'Cancel',
      onOk: () => placeCall(record, true)
    });
  };

  const startCall = (record) => {
    if (!record.aiCallConsent) confirmWithoutConsent(record);
    else placeCall(record, false);
  };

  const closeItem = (record, action) => withBusy(record.caseId, async () => {
    const res = await caseService.updateCallQueueItem(record.caseId, action);
    if (!res?.success) {
      message.error(res?.error || 'Failed to update');
      return;
    }
    message.success(action === 'done' ? 'Marked done' : 'Removed from Case Management');
    load({ quiet: true });
  });

  const openRecord = (record) => setMemberCard({ patientId: record.patientId, name: record.name });

  const numbersDiffer = settings?.textFromNumber && settings?.callFromNumber &&
    settings.textFromNumber !== settings.callFromNumber;

  const renderNextAction = (r) => {
    if (view === 'closed') {
      return <Tag>{r.closedReason === 'done' ? 'Done' : 'Removed'} · {timeAgo(r.closedAt)}</Tag>;
    }
    if (!r.textSentAt) {
      return (
        <NextActionButton
          icon={<FontAwesomeIcon icon={faCommentSms} />}
          disabled={!r.phone || busy[r.caseId]}
          onClick={() => openTextModal(r)}
        >
          Send text
        </NextActionButton>
      );
    }
    if (LIVE_STATUSES.includes(r.lastCall?.status)) {
      return (
        <NextActionButton
          type="primary"
          icon={<FontAwesomeIcon icon={faHeadphones} />}
          onClick={() => setLiveCaseId(r.caseId)}
        >
          Watch live
        </NextActionButton>
      );
    }
    const blocked = !settings?.aiCallingConfigured;
    return (
      <Tooltip title={blocked ? 'AI calling is not configured on the server' : ''}>
        <NextActionButton
          type={r.callCount ? 'default' : 'primary'}
          icon={<FontAwesomeIcon icon={faPhoneVolume} />}
          disabled={blocked || busy[r.caseId]}
          loading={busy[r.caseId]}
          onClick={() => startCall(r)}
        >
          {r.callCount ? 'Call again' : 'Start AI call'}
        </NextActionButton>
      </Tooltip>
    );
  };

  const rowMenu = (r) => ({
    items: [
      { key: 'open', icon: <FontAwesomeIcon icon={faArrowUpRightFromSquare} fixedWidth />, label: 'Open member record' },
      ...(r.lastCall
        ? [{ key: 'transcript', icon: <FontAwesomeIcon icon={faFileLines} fixedWidth />, label: LIVE_STATUSES.includes(r.lastCall.status) ? 'Watch live call' : 'View call transcript' }]
        : []),
      ...(view === 'active'
        ? [
            ...(r.textSentAt && r.phone
              ? [{ key: 'resend', icon: <FontAwesomeIcon icon={faCommentSms} fixedWidth />, label: 'Send text again' }]
              : []),
            { type: 'divider' },
            { key: 'done', icon: <FontAwesomeIcon icon={faCheck} fixedWidth />, label: 'Mark done' },
            { key: 'remove', icon: <FontAwesomeIcon icon={faXmark} fixedWidth />, label: 'Remove', danger: true }
          ]
        : [])
    ],
    onClick: ({ key }) => {
      if (key === 'open') openRecord(r);
      else if (key === 'transcript') setLiveCaseId(r.caseId);
      else if (key === 'resend') openTextModal(r);
      else if (key === 'done') closeItem(r, 'done');
      else if (key === 'remove') {
        Modal.confirm({
          title: `Remove ${r.name} from Case Management?`,
          content: 'They won\'t be texted or called from this list. You can add them again from the Members page.',
          okText: 'Remove',
          okButtonProps: { danger: true },
          onOk: () => closeItem(r, 'remove')
        });
      }
    }
  });

  const columns = [
    {
      title: 'Member',
      key: 'member',
      width: 240,
      render: (_, r) => (
        <MemberCell>
          <div className="mc-avatar">{initials(r.name)}</div>
          <div style={{ minWidth: 0 }}>
            <div className="mc-name" onClick={() => openRecord(r)}>{r.name}</div>
            <div className="mc-phone">{r.phone ? formatPhone(r.phone) : 'No phone on file'}</div>
            {!r.aiCallConsent && (
              <Tooltip title="They didn't check the AI voice call consent on their intake form">
                <Tag color="warning" style={{ marginTop: 4, fontSize: 11 }}>No AI call consent</Tag>
              </Tooltip>
            )}
          </div>
        </MemberCell>
      )
    },
    {
      title: 'Risk',
      key: 'risk',
      width: 120,
      render: (_, r) => {
        const level = r.riskLevel || 'unknown';
        const style = RISK_STYLES[level] || RISK_STYLES.unknown;
        return (
          <div>
            <RiskPill $color={style.color} $bg={style.bg}>{level === 'unknown' ? 'No data' : level}</RiskPill>
            {r.normalizedRiskScore != null && level !== 'unknown' && (
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4, paddingLeft: 4 }}>
                Score {r.normalizedRiskScore}/100
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Why they\'re here',
      key: 'why',
      render: (_, r) => {
        const src = SOURCES[r.source] || SOURCES.manual;
        return (
          <ReasonCell>
            <span className="rc-source">
              <FontAwesomeIcon icon={src.icon} style={{ color: COLORS.$deepTiffany }} />
              {src.label}
            </span>
            {r.criticalFindings?.length > 0
              ? r.criticalFindings.map((f) => <span key={f} className="rc-finding">{f}</span>)
              : <div>{r.reason}</div>}
            <span className="rc-time">Added {timeAgo(r.queuedAt)}</span>
          </ReasonCell>
        );
      }
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 280,
      render: (_, r) => {
        const step = callStep(r.lastCall);
        const unverifiable = ['exhausted', 'no_dob_on_file'].includes(r.lastCall?.verificationOutcome);
        const flags = [
          r.lastCall?.identityVerified && 'ID verified',
          unverifiable && 'Couldn\'t verify identity',
          r.lastCall?.transferred && 'Transferred to staff',
          r.callCount > 1 && `${r.callCount} calls`
        ].filter(Boolean);
        return (
          <div>
            <StepTrack>
              <Tooltip title={r.textBody || ''}>
                <StepDot $state={r.textSentAt ? 'done' : 'todo'}>
                  <FontAwesomeIcon icon={r.textSentAt ? faCircleCheck : faCommentSms} />
                  {r.textSentAt ? `Texted ${timeAgo(r.textSentAt)}` : 'Text'}
                </StepDot>
              </Tooltip>
              <span className="st-line" />
              <Tooltip title={r.lastCall ? (step.state === 'live' ? 'Watch live' : 'View transcript') : ''}>
                <StepDot
                  $state={step.state}
                  style={r.lastCall ? { cursor: 'pointer' } : undefined}
                  onClick={r.lastCall ? () => setLiveCaseId(r.caseId) : undefined}
                >
                  <FontAwesomeIcon icon={step.state === 'done' ? faCircleCheck : faPhone} />
                  {step.label}
                </StepDot>
              </Tooltip>
            </StepTrack>
            {(flags.length > 0 || r.lastCall?.summary) && (
              <Tooltip title={r.lastCall?.summary || ''}>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 6, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {flags.join(' · ')}{flags.length && r.lastCall?.summary ? ' — ' : ''}{r.lastCall?.summary}
                </div>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: 'Next step',
      key: 'next',
      width: 170,
      align: 'right',
      render: (_, r) => renderNextAction(r)
    },
    {
      title: '',
      key: 'menu',
      width: 56,
      align: 'center',
      render: (_, r) => (
        <Dropdown menu={rowMenu(r)} trigger={['click']} placement="bottomRight">
          <RowActionButton aria-label="More actions" disabled={busy[r.caseId]}>
            <MoreOutlined />
          </RowActionButton>
        </Dropdown>
      )
    }
  ];

  const statCards = [
    { key: 'all', label: 'To call', value: counts?.total, icon: faUsers, accent: '#1a1a1a' },
    { key: 'needs-text', label: 'Needs text', value: counts?.needsText, icon: faCommentSms, accent: COLORS.$mainTiffany },
    { key: 'ready', label: 'Ready to call', value: counts?.readyToCall, icon: faPhoneVolume, accent: COLORS.$deepTiffany },
    { key: 'called', label: 'Called', value: counts?.called, icon: faCircleCheck, accent: '#595959' }
  ];

  return (
    <div>
      <QueueHeader>
        <div>
          <div className="qh-title">Case Management</div>
          <div className="qh-sub">
            Members whose latest results scored high or critical. Text them first, then start the AI call.
          </div>
        </div>
        <div className="qh-right">
          {settings && (
            settings.transferEnabled ? (
              <ConfigChip>
                <FontAwesomeIcon icon={faHeadset} />
                Transfers to {formatPhone(settings.transferPhoneNumber)}
              </ConfigChip>
            ) : (
              <ConfigChip $warn>
                <FontAwesomeIcon icon={faHeadset} />
                No transfer number
              </ConfigChip>
            )
          )}
          <Button
            icon={<FontAwesomeIcon icon={faGear} />}
            onClick={() => setSettingsOpen(true)}
            style={{ height: 34, borderRadius: 999 }}
          >
            Call settings
          </Button>
        </div>
      </QueueHeader>

      {settings && !settings.aiCallingConfigured && (
        <Alert style={{ marginBottom: 16, borderRadius: 12 }} type="warning" showIcon message="AI calling (Vapi) isn't configured on the server, so calls can't be started." />
      )}
      {settings?.aiCallingConfigured && settings.webhookMisconfigured && (
        <Alert
          style={{ marginBottom: 16, borderRadius: 12 }}
          type="error"
          showIcon
          message={`AI call events are being sent to ${settings.webhookHost || 'nowhere'}, not this server. Calls can't verify members or read results, and their status won't update. Set VAPI_WEBHOOK_BASE_URL to this backend's public URL.`}
        />
      )}
      {numbersDiffer && (
        <Alert
          style={{ marginBottom: 16, borderRadius: 12 }}
          type="warning"
          showIcon
          message={`Texts go out from ${formatPhone(settings.textFromNumber)} but AI calls come from ${formatPhone(settings.callFromNumber)}. The text says "from this number", so these should match.`}
        />
      )}

      {view === 'active' && (
        <Row gutter={[16, 16]}>
          {statCards.map((c) => (
            <Col xs={12} lg={6} key={c.key}>
              <QueueStatCard $accent={c.accent} $active={stage === c.key} onClick={() => setStage(c.key)}>
                <div className="qs-icon"><FontAwesomeIcon icon={c.icon} /></div>
                <div>
                  <div className="qs-value">{c.value ?? '—'}</div>
                  <div className="qs-label">{c.label}</div>
                </div>
              </QueueStatCard>
            </Col>
          ))}
        </Row>
      )}

      <QueueToolbar>
        <Input
          className="qt-search"
          placeholder="Search by name or phone…"
          prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: COLORS.textMuted }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: 'To call', value: 'active' },
            { label: 'Done / removed', value: 'closed' }
          ]}
        />
      </QueueToolbar>

      <CaseTable
        columns={columns}
        dataSource={rows}
        rowKey="caseId"
        loading={{ spinning: loading, indicator: <OutreachHeartLoader /> }}
        locale={{
          emptyText: view === 'active'
            ? (stage === 'all'
                ? 'No one to call. Members whose new results score high or critical are added automatically, or add members from the Members page.'
                : 'No members at this step.')
            : 'Nothing here yet.'
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} members`,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          }
        }}
        className="event-management-pagination"
        scroll={{ x: 1100 }}
      />

      <Drawer
        title="Call settings"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        width={settingsTab === 'ai' ? 1040 : 480}
        destroyOnClose={false}
        footer={settingsTab !== 'ai' ? (
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button type="primary" loading={savingSettings} onClick={saveSettings}>Save settings</Button>
          </Space>
        ) : null}
      >
        <Tabs
          activeKey={settingsTab}
          onChange={(k) => {
            setSettingsTab(k);
            // The AI tab can also change the transfer number; refresh this form.
            if (k !== 'ai') loadSettings();
          }}
          style={{ marginTop: -12 }}
          items={[
            { key: 'calling', label: 'Texting & transfer' },
            { key: 'inbound', label: 'Inbound calls' },
            { key: 'ai', label: 'AI voice & prompt' }
          ]}
        />
        {settingsTab === 'ai' && <VoiceSettings />}
        {settingsTab === 'inbound' && (
          <>
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Who answers calls to our number</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 10 }}>
                {settings?.textFromNumber ? `When someone calls ${formatPhone(settings.textFromNumber)}` : 'When someone calls our number'}
              </div>
              <Segmented
                block
                value={inboundMode}
                onChange={setInboundMode}
                options={[
                  { value: 'browser', label: <span><FontAwesomeIcon icon={faHeadset} style={{ marginRight: 6 }} />Staff (browser)</span> },
                  { value: 'ai', label: <span><FontAwesomeIcon icon={faRobot} style={{ marginRight: 6 }} />AI agent</span> }
                ]}
              />
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 10, lineHeight: 1.6 }}>
                {inboundMode === 'ai'
                  ? 'The AI agent picks up, verifies the caller before sharing any results, and can transfer to your transfer number. If the AI can’t start, staff browsers ring instead.'
                  : 'Every staff member signed in to this app rings at once (like quick call). The first to answer takes the call.'}
              </div>
            </div>

            {inboundMode === 'browser' && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Ring for</div>
                <Segmented
                  value={ringSeconds}
                  onChange={setRingSeconds}
                  options={[15, 20, 30, 45].map((s) => ({ value: s, label: `${s} sec` }))}
                />
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontWeight: 600 }}>Missed call text</div>
                <Switch checked={missedTextEnabled} onChange={setMissedTextEnabled} />
              </div>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 10 }}>
                Texted to the caller when nobody answers in the browser. <code>{'{{firstName}}'}</code> becomes
                their first name when we recognize the number (otherwise &ldquo;there&rdquo;). Replies land in the Inbox.
              </div>
              {missedTextEnabled && (
                <>
                  <TextArea
                    rows={4}
                    maxLength={480}
                    showCount
                    value={missedTextTemplate}
                    onChange={(e) => setMissedTextTemplate(e.target.value)}
                  />
                  <SmsPreview style={{ marginTop: 16 }}>
                    <div className="sp-label">Preview</div>
                    <div className="sp-bubble">{renderText(missedTextTemplate, SAMPLE_NAME) || ' '}</div>
                  </SmsPreview>
                </>
              )}
            </div>

            {settings?.inbound && (
              <Alert
                style={{ marginTop: 8 }}
                type={settings.inbound.connected ? 'success' : 'warning'}
                showIcon
                message={settings.inbound.connected
                  ? 'Calls to our number are routed by these settings'
                  : 'Calls to our number are not reaching this system yet'}
                description={settings.inbound.connected ? null : (
                  <div style={{ fontSize: 13 }}>
                    <div style={{ marginBottom: 8 }}>
                      The number currently sends calls to{' '}
                      <code>{settings.inbound.currentUrl ? new URL(settings.inbound.currentUrl).host : 'nowhere'}</code>.
                    </div>
                    {settings.inbound.canConnect ? (
                      <Button size="small" type="primary" loading={connectingNumber} onClick={connectNumber}>
                        Route calls here
                      </Button>
                    ) : (
                      <div>Connect it from the live site.</div>
                    )}
                  </div>
                )}
              />
            )}
          </>
        )}
        {settingsTab === 'calling' && (
        <>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Transfer to a person</div>
          <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 10 }}>
            If the member asks for a person during the AI call, it&apos;s transferred to this number.
          </div>
          <Input
            size="large"
            placeholder="(555) 555-5555"
            prefix={<FontAwesomeIcon icon={faHeadset} style={{ color: COLORS.textMuted, marginRight: 4 }} />}
            value={transferNumber}
            onChange={(e) => setTransferNumber(e.target.value)}
            allowClear
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Pre-call text</div>
          <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 10 }}>
            Sent before the AI call. <code>{'{{firstName}}'}</code> becomes the member&apos;s first name.
          </div>
          <TextArea
            rows={4}
            maxLength={480}
            showCount
            value={textTemplate}
            onChange={(e) => setTextTemplate(e.target.value)}
          />
        </div>

        <SmsPreview>
          <div className="sp-label">Preview</div>
          <div className="sp-bubble">{renderText(textTemplate, SAMPLE_NAME) || ' '}</div>
        </SmsPreview>

        {settings && (
          <div style={{ marginTop: 20, fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7 }}>
            {settings.textFromNumber && <div>Texts are sent from {formatPhone(settings.textFromNumber)}</div>}
            {settings.callFromNumber && <div>AI calls come from {formatPhone(settings.callFromNumber)}</div>}
          </div>
        )}
        </>
        )}
      </Drawer>

      <LiveCallDrawer
        open={!!liveCaseId}
        member={rows.find((r) => String(r.caseId) === String(liveCaseId)) || (liveCaseId ? { caseId: liveCaseId } : null)}
        defaultTakeoverNumber={settings?.transferPhoneNumber}
        onClose={() => setLiveCaseId(null)}
        onChanged={refreshQuiet}
      />

      <MemberCardDrawer
        open={!!memberCard}
        patientId={memberCard?.patientId}
        name={memberCard?.name}
        onClose={() => setMemberCard(null)}
        onCaseChanged={refreshQuiet}
      />

      <Modal
        open={!!textModal}
        title={textModal ? `Text ${textModal.record.name}` : ''}
        okText="Send text"
        okButtonProps={{ disabled: !textModal?.body?.trim(), loading: textModal && busy[textModal.record.caseId] }}
        onOk={sendText}
        onCancel={() => setTextModal(null)}
        destroyOnClose
      >
        {textModal && (
          <>
            <div style={{ marginBottom: 10, color: COLORS.textMuted }}>To {formatPhone(textModal.record.phone)}</div>
            <TextArea
              rows={4}
              maxLength={480}
              showCount
              value={textModal.body}
              onChange={(e) => setTextModal((m) => ({ ...m, body: e.target.value }))}
            />
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 12 }}>
              Once the text is sent, you can start the AI call.
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CallQueueTab;
